
import React, { useState } from 'react';
import { useLanguage } from '../../../context/LanguageContext';
import { Loader2, Filter } from 'lucide-react';
import { usePermissions } from '../../../hooks/usePermissions';
import { canAssignEmployeeSkills } from '../../../utils/roleChecks';

// Hooks
import { useEmployees } from './hooks/useEmployees';

// Componentes
import { EmployeeTable } from './components/EmployeeTable';
import { EmployeeFiltersModal } from './components/EmployeeFiltersModal';
import { EmployeeDetails } from './components/EmployeeDetails';
import { EmployeeSkillsSection } from './components/EmployeeSkillsSection';
import { SkillsCatalogSection } from './components/SkillsCatalogSection';
import { TeamsSection } from './components/TeamsSection';

const TablaEmpleados = () => {
  const { t } = useLanguage();
  const { roleKeys } = usePermissions();
  const canAssignSkills = canAssignEmployeeSkills(roleKeys);
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [showCreateSkillModal, setShowCreateSkillModal] = useState(false);

  // Hook para gestión de empleados
  const {
    employees,
    loading,
    error,
    page,
    setPage,
    searchQuery,
    setSearchQuery,
    employeesToShow,
    totalItems,
    totalPages,
    rangeStart,
    rangeEnd,
    filters,
    setFilters,
    filterOptions,
    showFiltersModal,
    setShowFiltersModal,
    handleApplyFilters
  } = useEmployees();

  // Handler simple para cuando se hace click en un empleado
  const handleEmployeeClick = (employee) => {
    setSelectedEmployee(employee);
    // Por ahora solo guardamos el empleado seleccionado
    // En el futuro se podría mostrar detalles o navegar a otra página
  };

  return (
    <div className="p-4 sm:p-6 w-full min-w-0">
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-white mb-2">
              {t.personas?.empleados?.title || '🙍‍♂️ Empleados'}
            </h1>
            <p className="text-gray-400">
              {t.personas?.empleados?.description || 'Listado completo de empleados de la fábrica'}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShowFiltersModal(true)}
            className="inline-flex items-center justify-center p-2 rounded-lg border border-blue-600 bg-blue-600 text-white hover:bg-blue-700 transition-colors"
            title="Filtros"
          >
            <Filter className="w-4 h-4" />
          </button>
        </div>
      </div>

      {loading && (
        <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg p-12">
          <div className="flex flex-col items-center justify-center text-gray-400">
            <Loader2 className="animate-spin mb-4" size={32} />
            <p>{t.common?.loading || 'Cargando empleados...'}</p>
          </div>
        </div>
      )}

      {error && !loading && (
        <div className="rounded-2xl border border-gray-700 bg-gray-800 shadow-lg p-6 mb-6">
          <div className="rounded-lg border border-red-800 bg-red-900/20 p-4">
            <p className="text-red-300">
              <strong>{t.common?.error || 'Error'}:</strong> {error}
            </p>
          </div>
        </div>
      )}

      {!loading && !error && (
        <>
        <div className="w-full">
          <EmployeeTable
            employees={employees}
            employeesToShow={employeesToShow}
            totalItems={totalItems}
            totalPages={totalPages}
            page={page}
            setPage={setPage}
            rangeStart={rangeStart}
            rangeEnd={rangeEnd}
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            selectedEmployeeForRole={selectedEmployee}
            onEmployeeClick={handleEmployeeClick}
            listFilters={filters}
            filterOptions={filterOptions}
            onListFilterChange={(key, value) => setFilters((prev) => ({ ...prev, [key]: value }))}
          />
        </div>
        <TeamsSection employees={employees} />
        </>
      )}

      {/* Modal de filtros */}
      <EmployeeFiltersModal
        isOpen={showFiltersModal}
        onClose={() => setShowFiltersModal(false)}
        employees={employees}
        filters={filters}
        onApplyFilters={handleApplyFilters}
      />

      {selectedEmployee && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
          onClick={() => setSelectedEmployee(null)}
          role="presentation"
        >
          <div
            className="w-full max-w-4xl h-[min(700px,90vh)]"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-label="Detalles del empleado"
          >
            <EmployeeDetails
              employee={selectedEmployee}
              onClose={() => setSelectedEmployee(null)}
              extra={canAssignSkills ? (
                <EmployeeSkillsSection
                  embedded
                  selectedEmployee={selectedEmployee}
                  onCreateSkill={() => setShowCreateSkillModal(true)}
                />
              ) : null}
            />
          </div>
        </div>
      )}

      {canAssignSkills && (
        <SkillsCatalogSection
          hideList
          createModalOpen={showCreateSkillModal}
          onCreateModalChange={setShowCreateSkillModal}
        />
      )}
    </div>
  );
};

export default TablaEmpleados;
